import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import { fmtAsOf } from '../components/ContactCenter/format';
import PodMapping from '../components/ContactCenter/PodMapping';
import { btn, c, card, tbl } from '../theme';

// Contact Center · Settings — the values the section's pages are computed
// against, for holders of the Contact Center manage permission. The server
// checks every field against its range (services/ccSettings.js) and logs each
// change with what it was; this page shows the ranges and says what was
// refused, in the server's words.

function SettingForm({ title, blurb, setting, save }) {
  const [draft, setDraft] = useState(setting.value || {});
  const [state, setState] = useState(null);
  useEffect(() => { setDraft(setting.value || {}); }, [setting.value]);
  const dirty = Object.keys(setting.fields).some((k) => Number(draft[k]) !== Number(setting.value?.[k]));
  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true });
    try {
      await save(Object.fromEntries(Object.keys(setting.fields).map((k) => [k, Number(draft[k])])));
      setState({ ok: 'Saved.' });
    } catch (err) {
      setState({ error: err.response?.data?.error || err.message });
    }
  };
  return (
    <form style={s.card} onSubmit={submit} aria-label={title}>
      <h2 style={s.h2}>{title}</h2>
      <p style={s.blurb}>{blurb}</p>
      <div style={s.fields}>
        {Object.entries(setting.fields).map(([k, f]) => (
          <label key={k} style={s.field}>
            <span style={s.label}>{f.label}</span>
            <input id={`cc-set-${k}`} type="number" step={f.integer ? 1 : 'any'} min={f.min} max={f.max}
              value={draft[k] ?? ''} onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))} style={s.input} />
            <span style={s.range}>{f.min.toLocaleString('en-US')} – {f.max.toLocaleString('en-US')}</span>
          </label>
        ))}
      </div>
      <div style={s.row}>
        <button type="submit" disabled={!dirty || state?.busy} style={btn.primarySm}>Save</button>
        {state?.ok && <span style={s.ok}>{state.ok}</span>}
        {state?.error && <span style={s.error} role="alert">{state.error}</span>}
        {setting.updatedAt && <span style={s.meta}>Last changed {fmtAsOf(setting.updatedAt)}{setting.updatedBy ? ` by ${setting.updatedBy}` : ''}</span>}
      </div>
    </form>
  );
}

function InboxRow({ row, save }) {
  const [pct, setPct] = useState(String(row.sla_pct));
  const [hours, setHours] = useState(String(row.sla_seconds / 3600));
  const [state, setState] = useState(null);
  const dirty = Number(pct) !== row.sla_pct || Number(hours) * 3600 !== row.sla_seconds;
  const submit = async () => {
    setState({ busy: true });
    try {
      await save(row.queueId, { sla_pct: Number(pct), sla_seconds: Math.round(Number(hours) * 3600) });
      setState({ ok: 'Saved' });
    } catch (err) {
      setState({ error: err.response?.data?.error || err.message });
    }
  };
  return (
    <tr>
      <td style={s.td}>{row.name}</td>
      <td style={s.num}><input aria-label={`${row.name} share within target`} type="number" min={1} max={100} value={pct} onChange={(e) => setPct(e.target.value)} style={s.small} /> %</td>
      <td style={s.num}><input aria-label={`${row.name} target hours`} type="number" min={1} step="any" value={hours} onChange={(e) => setHours(e.target.value)} style={s.small} /> h</td>
      <td style={s.td}>
        <button type="button" disabled={!dirty || state?.busy} onClick={submit} style={btn.secondaryXs}>Save</button>
        {state?.ok && <span style={s.ok}> {state.ok}</span>}
        {state?.error && <span style={s.error} role="alert"> {state.error}</span>}
      </td>
    </tr>
  );
}

// The forecast: what is stored, by month; and a workbook upload that is
// previewed before anything is stored, then confirmed.
function Forecast() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['cc-forecast'], queryFn: () => api.get('/contact-center/settings/forecast') });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [state, setState] = useState(null);
  const send = (commit) => {
    const fd = new FormData();
    fd.append('file', file);
    if (commit) fd.append('commit', 'true');
    return api.upload('/contact-center/settings/forecast', fd);
  };
  const doPreview = async () => {
    setState({ busy: true });
    setPreview(null);
    try {
      setPreview((await send(false)).meta);
      setState(null);
    } catch (err) {
      setState({ error: err.response?.data?.error || err.message });
    }
  };
  const confirm = async () => {
    setState({ busy: true });
    try {
      const r = await send(true);
      setState({ ok: `Stored ${r.meta.days} days (${r.meta.firstDay} to ${r.meta.lastDay})${r.replacedDays ? `, replacing ${r.replacedDays} already stored` : ''}.` });
      setPreview(null);
      setFile(null);
      await qc.invalidateQueries({ queryKey: ['cc-forecast'] });
    } catch (err) {
      setState({ error: err.response?.data?.error || err.message });
    }
  };
  return (
    <section style={s.card} aria-label="Forecast">
      <h2 style={s.h2}>Forecast</h2>
      <p style={s.blurb}>
        Athens&rsquo; per-pod daily forecast workbook (the monthly &ldquo;PerPod_Forecast&rdquo; sheet). Leadership and Team
        Performance compare Handled with it on the days it covers. Uploading a month replaces those days; preview first.
      </p>
      <table style={s.table}>
        <thead><tr><th style={s.th}>Month</th><th style={s.th}>Source</th><th style={s.thNum}>Days</th><th style={s.thNum}>Forecast calls</th><th style={s.th}>Loaded</th></tr></thead>
        <tbody>
          {(q.data?.months || []).map((m) => (
            <tr key={m.month}><td style={s.td}>{m.month}</td><td style={s.td}>{m.label}</td><td style={s.num}>{m.days}</td>
              <td style={s.num}>{Number(m.org_total).toLocaleString('en-US')}</td><td style={s.td}>{fmtAsOf(m.loaded_at)}</td></tr>
          ))}
        </tbody>
      </table>
      <div style={s.row}>
        <input id="cc-forecast-file" type="file" accept=".xlsx" aria-label="Forecast workbook"
          onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setState(null); }} />
        <button type="button" disabled={!file || state?.busy} onClick={doPreview} style={btn.secondaryXs}>Preview</button>
        {state?.ok && <span style={s.ok}>{state.ok}</span>}
        {state?.error && <span style={s.error} role="alert">{state.error}</span>}
      </div>
      {preview && (
        <div style={s.preview} aria-label="Forecast preview">
          <p style={s.label}>{preview.file}: {preview.days} days, {preview.firstDay} to {preview.lastDay} · {preview.orgTotal.toLocaleString('en-US')} calls · &ldquo;{preview.label}&rdquo;</p>
          <p style={s.blurb}>
            {Object.entries(preview.perPodTotals).map(([pod, n]) => `${pod} ${n.toLocaleString('en-US')}`).join(' · ')}
          </p>
          {preview.skippedRows.length > 0 && <p style={s.blurb}>Rows not read as days: {preview.skippedRows.join('; ')}</p>}
          {preview.unmappedHeaders.length > 0 && <p style={{ ...s.blurb, color: c.warningText }}>Columns not recognised as pods, left out: {preview.unmappedHeaders.join(', ')}</p>}
          <button type="button" disabled={state?.busy} onClick={confirm} style={btn.primarySm}>Store this forecast</button>
        </div>
      )}
    </section>
  );
}

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// The summary email: on or off, which days, what time, to whom; a preview of
// today's email and a test sent to yourself.
function SummaryEmail({ setting, save }) {
  const [draft, setDraft] = useState(() => ({ ...setting.value, recipientsText: setting.value.recipients.join('\n') }));
  const [state, setState] = useState(null);
  const [preview, setPreview] = useState(null);
  const toggleDay = (d) => setDraft((x) => ({ ...x, days: x.days.includes(d) ? x.days.filter((y) => y !== d) : [...x.days, d].sort() }));
  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true });
    try {
      await save({
        enabled: draft.enabled, days: draft.days, time: draft.time,
        recipients: draft.recipientsText.split(/[\s,;]+/).map((r) => r.trim()).filter(Boolean),
      });
      setState({ ok: 'Saved.' });
    } catch (err) {
      setState({ error: err.response?.data?.error || err.message });
    }
  };
  const showPreview = async () => {
    setPreview({ loading: true });
    try {
      setPreview(await api.get('/contact-center/settings/summary-email/preview'));
    } catch (err) {
      setPreview({ error: err.response?.data?.error || err.message });
    }
  };
  const sendTest = async () => {
    setState({ busy: true });
    try {
      const r = await api.post('/contact-center/settings/summary-email/test', {});
      setState({ ok: `Test sent to ${r.sent}.` });
    } catch (err) {
      setState({ error: err.response?.data?.error || err.message });
    }
  };
  return (
    <form style={s.card} onSubmit={submit} aria-label="Summary email">
      <h2 style={s.h2}>Summary email</h2>
      <p style={s.blurb}>
        The Leadership figures for the day so far and the month to date, as an email, sent once on each chosen day at
        the chosen Pacific time. Athens addresses only. It stays off until you turn it on.
      </p>
      <label style={s.check}><input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft((x) => ({ ...x, enabled: e.target.checked }))} /> Send it</label>
      <div style={s.row} role="group" aria-label="Days">
        {DAY_NAMES.map((n, i) => (
          <label key={n} style={s.check}><input type="checkbox" checked={draft.days.includes(i + 1)} onChange={() => toggleDay(i + 1)} /> {n}</label>
        ))}
        <label style={s.check}>at <input id="cc-email-time" type="time" min="05:00" max="20:00" value={draft.time}
          onChange={(e) => setDraft((x) => ({ ...x, time: e.target.value }))} style={s.small} /> Pacific</label>
      </div>
      <label style={s.field}><span style={s.label}>Recipients, one per line</span>
        <textarea id="cc-email-recipients" rows={4} value={draft.recipientsText}
          onChange={(e) => setDraft((x) => ({ ...x, recipientsText: e.target.value }))} style={s.input} placeholder="name@athensservices.com" /></label>
      <div style={s.row}>
        <button type="submit" disabled={state?.busy} style={btn.primarySm}>Save</button>
        <button type="button" onClick={showPreview} style={btn.secondaryXs}>Preview today&rsquo;s email</button>
        <button type="button" onClick={sendTest} disabled={state?.busy} style={btn.secondaryXs}>Send me a test</button>
        {state?.ok && <span style={s.ok}>{state.ok}</span>}
        {state?.error && <span style={s.error} role="alert">{state.error}</span>}
      </div>
      {preview?.error && <span style={s.error}>{preview.error}</span>}
      {preview?.html && (
        <div>
          <p style={s.label}>Subject: {preview.subject}</p>
          <iframe title="Summary email preview" sandbox="" srcDoc={preview.html} style={{ width: '100%', height: '520px', border: `1px solid ${c.border}`, borderRadius: '8px' }} />
        </div>
      )}
    </form>
  );
}

const VIEW_LABELS = { now: 'Right now', today: 'Today so far', pods: 'Pods', top: 'Top performers (names)' };

// TV walls: pair the screen showing a code, see when each last checked in,
// and revoke one. A screen shows its code at /tv.
function Displays() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['cc-displays'], queryFn: () => api.get('/contact-center/displays') });
  const [form, setForm] = useState({ code: '', label: '', deptId: 'support', views: ['now', 'today', 'pods'] });
  const [state, setState] = useState(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['cc-displays'] });
  const pair = async (e) => {
    e.preventDefault();
    setState({ busy: true });
    try {
      await api.post('/contact-center/displays/pair', form);
      setForm((f) => ({ ...f, code: '', label: '' }));
      setState({ ok: 'Paired. The screen shows its wall within a few seconds.' });
      await refresh();
    } catch (err) {
      setState({ error: err.response?.data?.error || err.message });
    }
  };
  const revoke = async (d) => {
    if (!window.confirm(`Stop "${d.label}" showing the wall?`)) return;
    await api.post(`/contact-center/displays/${d.id}/revoke`, {});
    await refresh();
  };
  const toggleView = (v) => setForm((f) => ({ ...f, views: f.views.includes(v) ? f.views.filter((x) => x !== v) : [...f.views, v] }));
  return (
    <section style={s.card} aria-label="TV wall displays">
      <h2 style={s.h2}>TV wall displays</h2>
      <p style={s.blurb}>
        Open <b>/tv</b> on the screen: it shows an eight-character code. Enter it here to pair that screen. A paired screen
        reads its wall and nothing else, and stops the moment it is revoked. Pairing lasts 180 days. Top performers puts
        first names and last initials on the wall; leave it off for screens visitors can see.
      </p>
      <form onSubmit={pair} style={s.fields} aria-label="Pair a display">
        <label style={s.field}><span style={s.label}>Code on the screen</span>
          <input id="cc-disp-code" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} style={s.input} placeholder="ABCD 2345" /></label>
        <label style={s.field}><span style={s.label}>Name</span>
          <input id="cc-disp-label" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} style={s.input} placeholder="Support floor, east wall" /></label>
        <label style={s.field}><span style={s.label}>Department</span>
          <select id="cc-disp-dept" value={form.deptId} onChange={(e) => setForm((f) => ({ ...f, deptId: e.target.value }))} style={s.input}>
            <option value="support">Support</option><option value="obc">OBC</option><option value="collections">Collections</option>
          </select></label>
        <fieldset style={{ ...s.field, border: 'none', padding: 0, margin: 0 }}><legend style={s.label}>Views</legend>
          {Object.entries(VIEW_LABELS).map(([v, label]) => (
            <label key={v} style={s.check}><input type="checkbox" checked={form.views.includes(v)} onChange={() => toggleView(v)} /> {label}</label>
          ))}
        </fieldset>
        <div style={s.row}>
          <button type="submit" disabled={!form.code || !form.label || !form.views.length || state?.busy} style={btn.primarySm}>Pair</button>
          {state?.ok && <span style={s.ok}>{state.ok}</span>}
          {state?.error && <span style={s.error} role="alert">{state.error}</span>}
        </div>
      </form>
      <table style={s.table}>
        <thead><tr><th style={s.th}>Screen</th><th style={s.th}>Department</th><th style={s.th}>Views</th><th style={s.th}>Last seen</th><th style={s.th}>Paired until</th><th style={s.th} /></tr></thead>
        <tbody>
          {(q.data?.displays || []).map((d) => (
            <tr key={d.id} style={{ opacity: d.revoked_at ? 0.5 : 1 }}>
              <td style={s.td}>{d.label}</td>
              <td style={s.td}>{d.dept_id}</td>
              <td style={s.td}>{d.views.join(', ')}</td>
              <td style={s.td}>{d.last_seen_at ? fmtAsOf(d.last_seen_at) : 'never'}</td>
              <td style={s.td}>{d.revoked_at ? 'revoked' : fmtAsOf(d.expires_at)}</td>
              <td style={s.td}>{!d.revoked_at && <button type="button" onClick={() => revoke(d)} style={btn.secondaryXs}>Revoke</button>}</td>
            </tr>
          ))}
          {!q.data?.displays?.length && <tr><td colSpan={6} style={s.td}>No screens paired yet.</td></tr>}
        </tbody>
      </table>
    </section>
  );
}

export default function ContactCenterSettings() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['cc-settings'], queryFn: () => api.get('/contact-center/settings') });
  const refresh = () => qc.invalidateQueries({ queryKey: ['cc-settings'] });
  const saveCost = async (v) => { await api.put('/contact-center/settings/cost_model', v); await refresh(); };
  const saveThresholds = async (v) => { await api.put('/contact-center/settings/live_thresholds', v); await refresh(); };
  const saveInbox = async (id, v) => { await api.put(`/contact-center/settings/inboxes/${id}`, v); await refresh(); };
  const saveEmail = async (v) => { await api.put('/contact-center/settings/summary_email', v); await refresh(); };
  const d = q.data;

  return (
    <div style={s.page}>
      <PageHeader eyebrow="Contact Center" title="Settings"
        subtitle="What the Contact Center pages are measured against. Every change is logged with what it was." />
      {q.isLoading && <LoadingSpinner />}
      {q.error && <div style={s.error}>Could not load the settings: {q.error.message}</div>}
      {d && (
        <>
          <SettingForm title="Cost per call model" setting={d.cost_model} save={saveCost}
            blurb="Leadership's modelled cost per call: wage ÷ 3,600 × AHT + yearly platform cost ÷ yearly calls. A model, not a Finance actual." />
          <SettingForm title="Live Ops flags" setting={d.live_thresholds} save={saveThresholds}
            blurb="Live Ops marks an agent in red when their AHT today passes the first figure, or they have been available longer than the second." />
          <section style={s.card} aria-label="Email inbox targets">
            <h2 style={s.h2}>Email inbox targets</h2>
            <p style={s.blurb}>
              Each inbox is judged on the share of email answered within its target time. These began as the targets Genesys
              held in May 2026 (80% within 24 hours); the portal cannot read them from Genesys yet.
            </p>
            <table style={s.table}>
              <thead><tr><th style={s.th}>Inbox</th><th style={s.thNum}>Share within target</th><th style={s.thNum}>Target time</th><th style={s.th} /></tr></thead>
              <tbody>{d.inboxes.rows.map((r) => <InboxRow key={r.queueId} row={r} save={saveInbox} />)}</tbody>
            </table>
          </section>
          <Forecast />
          <SummaryEmail setting={d.summary_email} save={saveEmail} />
          <Displays />
          <PodMapping />
          <p style={s.blurb}>
            Service-level, AHT and abandon-rate colours follow the targets agreed for the rewrite (service level 95%, AHT 5:00,
            abandon rate 3%) and are the same on every page.
          </p>
        </>
      )}
    </div>
  );
}

const s = {
  page:   { maxWidth: '980px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '14px' },
  card:   { ...card.base, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '10px' },
  h2:     { fontSize: '14px', fontWeight: 650, color: c.text, margin: 0 },
  blurb:  { fontSize: '12px', color: c.textMuted, margin: 0, lineHeight: 1.5 },
  fields: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' },
  field:  { display: 'flex', flexDirection: 'column', gap: '4px' },
  label:  { fontSize: '12px', fontWeight: 600, color: c.textStrong },
  range:  { fontSize: '10.5px', color: c.textFaint },
  input:  { fontSize: '13px', padding: '6px 8px', border: `1px solid ${c.borderStrong}`, borderRadius: '6px', fontFamily: 'inherit', color: c.textStrong },
  small:  { width: '80px', fontSize: '12px', padding: '4px 6px', border: `1px solid ${c.borderStrong}`, borderRadius: '6px', fontFamily: 'inherit', textAlign: 'right' },
  row:    { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
  ok:     { fontSize: '12px', color: c.successText },
  error:  { fontSize: '12px', color: c.dangerText },
  meta:   { fontSize: '11px', color: c.textMuted, marginLeft: 'auto' },
  table:  { width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' },
  th:     { ...tbl.thSm, textAlign: 'left' },
  thNum:  { ...tbl.thRSm },
  td:     { ...tbl.tdSm, color: c.textStrong },
  num:    { ...tbl.tdRSm, color: c.textStrong },
  check:  { fontSize: '12px', color: c.textStrong, display: 'block' },
  preview:{ background: c.surfaceSunk, borderRadius: '8px', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '6px' },
};
